import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Droplet, Save } from "lucide-react";
import { WB_TYPES, resolveAll, catLabel } from "../client/waterChem";

const catOf = (type) => { const t = (type || "").toLowerCase(); if (t.includes("swim")) return "swim_spa"; if (t.includes("spa")) return "spa"; return "pool"; };

export default function WaterSetup() {
  const [tpls, setTpls] = useState({});
  const [type, setType] = useState(WB_TYPES[0]);
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api("GET", "/water-templates").then((l) => { const m = {}; (l || []).forEach((t) => { m[t.type] = t; }); setTpls(m); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const category = catOf(type);
  const rows = useMemo(() => resolveAll({ type, category, range_overrides: {} }, tpls), [type, category, tpls]);
  useEffect(() => { const s = {}; rows.forEach((p) => { s[p.key] = { enabled: p.enabled, min: p.min, max: p.max }; }); setState(s); }, [type, tpls]); // eslint-disable-line

  const upd = (k, field, v) => setState((s) => ({ ...s, [k]: { ...s[k], [field]: v } }));
  const save = async () => {
    setSaving(true);
    const params = {};
    rows.forEach((p) => { params[p.key] = { enabled: !!state[p.key]?.enabled, min: parseFloat(state[p.key]?.min), max: parseFloat(state[p.key]?.max) }; });
    try { await api("PATCH", `/water-templates/${encodeURIComponent(type)}`, { category, params }); toast.success(`${type} ranges saved`); load(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div data-testid="water-setup" className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1"><Droplet className="h-6 w-6 text-sky-600" /><h1 className="font-display text-2xl font-bold text-slate-900">Water Test Setup</h1></div>
      <p className="text-sm text-slate-500 mb-4">Set the ideal Min/Max and which tests apply for each body-of-water type. Every matching pool/spa inherits these — staff can still override a specific pool on its Water Test tab.</p>
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
        <div className="space-y-1">
          {WB_TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)} data-testid={`ws-type-${t.replace(/\s/g, "-")}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-bold border ${t === type ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-200 hover:border-sky-300"}`}>
              {t} <span className="text-[10px] opacity-70">({catLabel(catOf(t))})</span>
            </button>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex justify-between items-center mb-3"><h2 className="font-bold text-slate-800">{type}</h2>
            <button onClick={save} disabled={saving} data-testid="ws-save" className="rounded-lg bg-emerald-600 disabled:bg-slate-300 text-white font-bold px-4 py-2 flex items-center gap-1.5"><Save className="h-4 w-4" /> Save</button></div>
          <div className="grid grid-cols-[1.6fr_0.6fr_0.9fr_0.9fr] gap-2 text-[11px] font-black text-slate-500 border-b pb-1"><span>Test</span><span className="text-center">Apply</span><span className="text-center">Min</span><span className="text-center">Max</span></div>
          <div className="divide-y divide-slate-100">
            {rows.map((p) => (
              <div key={p.key} className="grid grid-cols-[1.6fr_0.6fr_0.9fr_0.9fr] gap-2 items-center py-1.5" data-testid={`ws-row-${p.key}`}>
                <span className="text-[13px]">{p.label}</span>
                <input type="checkbox" checked={!!state[p.key]?.enabled} onChange={(e) => upd(p.key, "enabled", e.target.checked)} data-testid={`ws-enable-${p.key}`} className="mx-auto h-4 w-4" />
                <input value={state[p.key]?.min ?? ""} onChange={(e) => upd(p.key, "min", e.target.value)} data-testid={`ws-min-${p.key}`} className="rounded border border-slate-300 px-1 py-0.5 text-[12px] text-center" inputMode="decimal" />
                <input value={state[p.key]?.max ?? ""} onChange={(e) => upd(p.key, "max", e.target.value)} data-testid={`ws-max-${p.key}`} className="rounded border border-slate-300 px-1 py-0.5 text-[12px] text-center" inputMode="decimal" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
